import React from 'react';
import useThemeCanvas, { hexToRgb } from '../useThemeCanvas';

// Bokeh — soft out-of-focus light orbs, drifting slowly with depth. Layered so
// bigger (closer) orbs move faster while smaller (farther) orbs move slower,
// giving a cinematic parallax feel.

const COUNT = 40;

function spawn(w, h) {
  const depth = Math.random();  // 0 = far, 1 = near
  return {
    x: Math.random() * w,
    y: Math.random() * h,
    depth,
    r: 18 + depth * 70,
    speed: 0.1 + depth * 0.5,
    phase: Math.random() * Math.PI * 2,
    palettePick: Math.floor(Math.random() * 12),
    dirX: (Math.random() - 0.3), // tend to drift slightly rightward
    dirY: (Math.random() - 0.5) * 0.4,
  };
}

export default function BokehTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: (ctx, w, h, state) => {
      state.orbs = Array.from({ length: COUNT }, () => spawn(w, h));
      // Sort so near orbs draw on top
      state.orbs.sort((a, b) => a.depth - b.depth);
    },
    draw: (ctx, w, h, state, t) => {
      // Moody gradient backdrop
      const grad = ctx.createLinearGradient(0, 0, w, h);
      if (dark) {
        grad.addColorStop(0, '#0b0614');
        grad.addColorStop(0.5, '#1b0a2f');
        grad.addColorStop(1, '#07172b');
      } else {
        grad.addColorStop(0, '#1e1b4b');
        grad.addColorStop(0.5, '#4c1d5e');
        grad.addColorStop(1, '#312e81');
      }
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // 'screen' brightens without the harsh white clipping of 'lighter'.
      ctx.globalCompositeOperation = 'screen';

      for (const o of state.orbs) {
        const nx = o.dirX * o.speed;
        const ny = o.dirY * o.speed + Math.sin(t * 0.4 + o.phase) * 0.15;
        o.x += nx;
        o.y += ny;
        if (o.x > w + o.r) o.x = -o.r;
        if (o.x < -o.r) o.x = w + o.r;
        if (o.y > h + o.r) o.y = -o.r;
        if (o.y < -o.r) o.y = h + o.r;

        // Breathe
        const breath = 0.85 + 0.15 * Math.sin(t * 0.6 + o.phase);
        const R = o.r * breath;

        // Color: palette pick, softened by depth
        const idx = (o.palettePick + (colorMode === 'cycling' ? Math.floor(t * 0.4) : 0)) % palette.length;
        const rgb = hexToRgb(palette[idx]);
        const alpha = (dark ? 0.3 : 0.34) + o.depth * 0.24;

        // Soft radial gradient — hollow rim for bokeh ring feel
        const rg = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, R);
        rg.addColorStop(0, `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha * 0.6})`);
        rg.addColorStop(0.6, `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`);
        rg.addColorStop(0.85, `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha * 0.4})`);
        rg.addColorStop(1, `rgba(${rgb.r},${rgb.g},${rgb.b},0)`);
        ctx.fillStyle = rg;
        ctx.beginPath();
        ctx.arc(o.x, o.y, R, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = 'source-over';
    },
  });
  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
